import * as React from "react";
import * as _ from "lodash";
import styled, { css } from "styled-components";
import moment from "moment-timezone";

import { Text } from "src/library/components/Text";
import { Button } from "src/library/components/Button";
import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import {
  getScheduleNaturalLanguage,
  validateSchedule
} from "src/core/schedules/utils";
import {
  ordersApi,
  CountOrdersResult,
  IOrderStatus
} from "src/core/orders/api/orders.api";
import { getOrderStatusIdsByGroup } from "src/core/orders/utils/orderStatus";
import {
  ISchedule,
  SchedulePreRequestUpdate,
  useScheduleApi
} from "src/core/schedules/api/schedules.api";
import ScheduleWarningModal from "src/core/schedules/components/ScheduleWarningModal";
import { IList } from "src/core/lists/api/lists.api";
import { IDrop } from "src/core/drops/api/drops.api";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import HtmlBody from "src/library/components/Notes/HtmlBody";

interface ISScheduleDetailInfoProps {
  schedule: ISchedule | null;
  terminology: ITerminology;
  list: IList | null;
  drop: IDrop;
  setScheduleViewSetting: (type: "detail" | "edit") => void;
  orderStatuses: IOrderStatus[];
  isScheduleLoading: boolean;
  isLoading: boolean;
  handleSuspendSchedule: (id: number) => void;
  handleReactivateSchedule: (id: number) => void;
}

const ScheduleDetailFormInfo: React.ReactFCWithChildren<ISScheduleDetailInfoProps> = ({
  schedule,
  terminology,
  list,
  drop,
  setScheduleViewSetting,
  orderStatuses,
  isScheduleLoading,
  isLoading,
  handleSuspendSchedule,
  handleReactivateSchedule
}) => {
  const checkUserPermission = useCheckUserPermission();
  const [ countOrdersResponse, countOrders ] = ordersApi<CountOrdersResult>("COUNT_ORDERS");
  const [ suspendWarningModal, setSuspendWarningModal ] = React.useState<SchedulePreRequestUpdate | null>(null);
  const [ preReActivateSchedule, setPreReActivateSchedule ] = useScheduleApi("PRE_UPDATE_SCHEDULE");
  const [ reactivateWarningModal, setReactivateWarningModal ] = React.useState<SchedulePreRequestUpdate | null>(null);

  React.useEffect(() => {
    if (countOrdersResponse.data) {
      setSuspendWarningModal({ toCancel: countOrdersResponse.data.count });
    }
  }, [ countOrdersResponse ]);

  React.useEffect(() => {
    if (preReActivateSchedule.data) {
      setReactivateWarningModal(preReActivateSchedule.data);
    }
  }, [ preReActivateSchedule ]);

  const handleSuspend = () => {
    if (list) {
      countOrders({
        filters: {
          listId: list.id,
          orderStatusId: { $in: getOrderStatusIdsByGroup(orderStatuses, "PROVISIONAL") },
          orderDeliveryDate: { $gte: moment.utc().toDate() }
        }
      }, { cache: { maxAge: 0 } });
    }
  };

  const handleReActivate = () => {
    if (schedule) {
      setPreReActivateSchedule({
        schedule: {
          ...schedule,
          id: undefined
        }
      });
    }
  };

  // Check if the creation of a schedule is possible.
  // If not, don't bother about showing any schedule info.
  if (list) {
    const { error: scheduleNotValid, message } = validateSchedule(list, drop, terminology);

    if (scheduleNotValid) {
      return (
        <ScheduleDays>
          <Text>
            Not available
          </Text>

          <Text>
            {message}
          </Text>
        </ScheduleDays>
      );
    }
  }

  const {
    frequencyDays, startAndEndDates, detachedOrders
  } = getScheduleNaturalLanguage(schedule, list?.hasDetachedOrders || false, terminology);

  const isSuspendButtonLoading = isScheduleLoading || countOrdersResponse.pending;
  const isReactivateButtonLoading = isScheduleLoading || preReActivateSchedule.pending;
  const isActiveSchedule = schedule && schedule.status === "active";

  return (
    <ScheduleDays>
      <ScheduleWarningModal
        data={suspendWarningModal}
        handleDismiss={() => setSuspendWarningModal(null)}
        onConfirm={() => (schedule && schedule.id ? handleSuspendSchedule(schedule.id) : null)}
      />

      <ScheduleWarningModal
        data={reactivateWarningModal}
        handleDismiss={() => setReactivateWarningModal(null)}
        onConfirm={() => (schedule && schedule.id ? handleReactivateSchedule(schedule.id) : null)}
      />

      {isActiveSchedule && (
        <>
          <Text>
            {frequencyDays}
          </Text>

          {!!startAndEndDates && (
            <Text>
              {startAndEndDates}
            </Text>
          )}

          {!!detachedOrders && (
            <Text weight="bold">
              <HtmlBody body={detachedOrders} />
            </Text>
          )}
        </>
      )}

      {checkUserPermission("amend-list") && (
        <>
          <ButtonWrapper>
            {!!isActiveSchedule && (
              <>
                <div>
                  <Button
                    disabled={isLoading}
                    $buttonType="secondary"
                    onClick={() => setScheduleViewSetting("edit")}
                  >
                    <>
                      {`Change ${terminology.schedule.singular.text}`}
                    </>
                  </Button>
                </div>
              </>
            )}

            <p />

            {isActiveSchedule ? (
              <ButtonWrapper>
                <Text>
                  {`You can cancel your ${terminology.schedule.singular.text}, which will cancel upcoming ${terminology.order.plural.text} past cut-off associated to this ${terminology.schedule.singular.text}.`}
                </Text>

                <div>
                  <Button
                    disabled={isLoading}
                    loading={isSuspendButtonLoading}
                    $buttonType="secondary"
                    onClick={() => handleSuspend()}
                  >
                    <>
                      Cancel
                    </>
                  </Button>
                </div>
              </ButtonWrapper>
            ) : (
              <ButtonWrapper>
                <Text>
                  {`Reactivate your ${terminology.schedule.singular.text} to generate ${terminology.order.plural.text} from this ${terminology.list.singular.text} based on the schedule above`}
                </Text>

                <div>
                  <Button
                    disabled={isLoading}
                    loading={isReactivateButtonLoading}
                    $buttonType="secondary"
                    onClick={() => handleReActivate()}
                  >
                    <>
                      Reactivate
                    </>
                  </Button>
                </div>
              </ButtonWrapper>
            )}
          </ButtonWrapper>
        </>
      )}
    </ScheduleDays>
  );
};

const ScheduleDays = styled.div`
  ${({ theme }) => css`
    .error {
      color: ${theme.colors.error};
      font-weight: bold;
      font-size: ${theme.fonts.sizes.h4};
    }

    .highlight-warn {
      color: ${theme.colors.warn};
      font-weight: bold;
    }

    > * {
      display: block;
      padding-bottom: ${theme.spacing.padding.md};

      &:first-letter {
        text-transform: uppercase;
      }
    }
  `}
`;

const ButtonWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    margin-top: ${theme.spacing.padding.sm};
    flex-direction: column;
    > div {
      margin-top: ${theme.spacing.padding.sm};
    }
  `}
`;

export default ScheduleDetailFormInfo;
